import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Empty,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ClearOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  adminApi,
  TestDataInventory,
  TestDataPatient,
  TestDataCleanupReport,
} from '../../api/adminApi';

const { Title, Paragraph, Text } = Typography;

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleString('ro-RO') : '-';

/**
 * Curățarea datelor de test lăsate în registru.
 *
 * Datele de test apar înregistrul de dezvoltare pentru că testele (HTTP și
 * Playwright) bat în API-ul de dezvoltare. Instrumentul identifică pacienții
 * de test după marcajele lor (CNP de test, e-mail @test.local, nume de test)
 * și îi șterge împreună cu formularele, leziunile și documentele lor.
 */
const TestDataCleanupPage = () => {
  const [inventory, setInventory] = useState<TestDataInventory | null>(null);
  const [report, setReport] = useState<TestDataCleanupReport | null>(null);
  const [includeOrphans, setIncludeOrphans] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const handleScan = async () => {
    try {
      setScanning(true);
      setReport(null);
      const data = await adminApi.getTestDataInventory();
      setInventory(data);
      if (data.patients.length === 0 && data.orphanForms.length === 0) {
        message.success('Nu există date de test de curățat.');
      }
    } catch {
      message.error('Nu a putut fi citit inventarul datelor de test.');
    } finally {
      setScanning(false);
    }
  };

  const handleCleanup = async () => {
    try {
      setCleaning(true);
      const data = await adminApi.cleanupTestData(includeOrphans);
      setReport(data);
      setInventory({
        patients: data.patients,
        orphanForms: data.orphanForms,
        counts: data.counts,
      });
      message.success('Curățarea datelor de test s-a încheiat.');
    } catch {
      message.error('Curățarea datelor de test a eșuat.');
    } finally {
      setCleaning(false);
    }
  };

  const orphanColumns = [
    {
      title: 'Tip',
      dataIndex: 'kind',
      key: 'kind',
      render: (value: string) => (value === 'dermato' ? 'Dermato' : 'Hepato'),
    },
    {
      title: 'ID formular',
      dataIndex: 'id',
      key: 'id',
      render: (value: string) => <Text code>{value}</Text>,
    },
    {
      title: 'Creat',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string | null) => formatDate(value),
    },
  ];

  const columns = [
    {
      title: 'Nume',
      dataIndex: 'lastName',
      key: 'lastName',
      render: (value: string | null, row: TestDataPatient) => (
        <>
          {value || '-'}
          {row.softDeleted && (
            <Tag color="default" style={{ marginLeft: 8 }}>
              șters logic
            </Tag>
          )}
        </>
      ),
    },
    {
      title: 'Prenume',
      dataIndex: 'firstName',
      key: 'firstName',
      render: (value: string | null) => value || '-',
    },
    {
      title: 'CNP',
      dataIndex: 'cnp',
      key: 'cnp',
      render: (value: string | null) => value || '-',
    },
    {
      title: 'Motiv',
      dataIndex: 'reason',
      key: 'reason',
      render: (value: string) => <Tag color="orange">{value}</Tag>,
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Title level={3}>Curățare date de test</Title>
        <Paragraph type="secondary" style={{ maxWidth: 900 }}>
          Testele rulează în API-ul de dezvoltare, deci pot lăsa pacienți și formulare
          în registru. Instrumentul de mai jos identifică datele de test după marcajele
          lor (CNP de test, e-mail <Text code>@test.local</Text>, nume de test) și le
          șterge împreună cu formularele, leziunile și documentele asociate. Datele
          reale și datele demo nu sunt atinse.
        </Paragraph>
      </div>

      <Alert
        type="warning"
        showIcon
        icon={<WarningOutlined />}
        message="Ștergerea este definitivă"
        description="Formularele șterse logic prin API rămân în baza de date, de aceea instrumentul le șterge definitiv din baza de date. Nu există recuperare — verificați inventarul înainte de a curăța."
      />

      <Card
        title="Inventar"
        extra={
          <Space>
            <Button icon={<SearchOutlined />} loading={scanning} onClick={handleScan}>
              Verifică datele de test
            </Button>
            <Button
              type="primary"
              danger
              icon={<ClearOutlined />}
              loading={cleaning}
              disabled={!inventory || (inventory.patients.length === 0 && inventory.orphanForms.length === 0)}
              onClick={handleCleanup}
            >
              Curăță datele de test
            </Button>
          </Space>
        }
      >
        <Checkbox
          checked={includeOrphans}
          onChange={(event) => setIncludeOrphans(event.target.checked)}
          style={{ marginBottom: 16 }}
        >
          Include și formularele orfane (fără pacient asociat)
        </Checkbox>

        {inventory ? (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={4}>
                <Statistic title="Pacienți de test" value={inventory.patients.length} />
              </Col>
              <Col span={4}>
                <Statistic title="Formulare dermato" value={inventory.counts.dermatoForms} />
              </Col>
              <Col span={4}>
                <Statistic title="Formulare hepato" value={inventory.counts.hepatoForms} />
              </Col>
              <Col span={4}>
                <Statistic title="Leziuni" value={inventory.counts.lesions} />
              </Col>
              <Col span={4}>
                <Statistic title="Documente" value={inventory.counts.documents} />
              </Col>
              <Col span={4}>
                <Statistic title="Formulare orfane" value={inventory.counts.orphanForms} />
              </Col>
            </Row>

            <Table
              rowKey="id"
              size="small"
              columns={columns}
              dataSource={inventory.patients}
              pagination={false}
              locale={{
                emptyText: <Empty description="Nu au fost găsiți pacienți de test." />,
              }}
            />

            {inventory.orphanForms.length > 0 && (
              <>
                <Paragraph type="secondary" style={{ marginTop: 24, marginBottom: 8 }}>
                  Formulare orfane (fără pacient asociat) — șterse doar dacă bifezi
                  opțiunea de mai sus.
                </Paragraph>
                <Table
                  rowKey="id"
                  size="small"
                  columns={orphanColumns}
                  dataSource={inventory.orphanForms}
                  pagination={false}
                />
              </>
            )}
          </>
        ) : (
          <Empty description="Apăsați „Verifică datele de test” pentru a vedea ce ar fi șters." />
        )}
      </Card>

      {report && (
        <Card title="Rezultatul curățării">
          <Descriptions
            bordered
            size="small"
            column={3}
            items={[
              { key: 'p', label: 'Pacienți șterși', children: report.deleted.patients },
              { key: 'fd', label: 'Formulare dermato', children: report.deleted.dermatoForms },
              { key: 'fh', label: 'Formulare hepato', children: report.deleted.hepatoForms },
              { key: 'l', label: 'Leziuni', children: report.deleted.lesions },
              { key: 'd', label: 'Documente', children: report.deleted.documents },
            ]}
          />
          {report.warnings.length > 0 && (
            <Alert
              style={{ marginTop: 16 }}
              type="warning"
              showIcon
              icon={<ExclamationCircleOutlined />}
              message="Unele operații au necesitat atenție"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {report.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              }
            />
          )}
        </Card>
      )}
    </Space>
  );
};

export default TestDataCleanupPage;
